import React, { useState, useEffect } from 'react';
import type { CmsPage } from './types';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { CookieConsent } from './components/CookieConsent';
import { SearchModal } from './components/SearchModal';
import { AdminLoginModal } from './components/AdminLoginModal';
import { HomePage } from './pages/HomePage';
import { CollectionViewPage } from './pages/CollectionViewPage';
import { MapsAtlasPage } from './pages/MapsAtlasPage';
import { CmsPageView } from './pages/CmsPageView';
import { NotFoundPage } from './pages/NotFoundPage';
import { ArrowUp } from 'lucide-react';
import { getAdminToken, clearAdminToken, verifyAdminToken } from './lib/auth';

const KNOWN_COLLECTIONS = new Set([
  'items',
  'enemies',
  'npcs',
  'abilities',
  'skills',
  'classes',
  'races',
  'quests',
  'haunts',
  'cities',
  'city-venues',
  'city-districts',
  'conditions',
  'animals',
  'backgrounds',
  'lexicon',
  'organizations',
  'tags',
]);

export const App: React.FC = () => {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminLoginOpen, setIsAdminLoginOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [allPages, setAllPages] = useState<CmsPage[]>([]);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Validate saved admin token on initial mount
  useEffect(() => {
    const token = getAdminToken();
    if (token) {
      verifyAdminToken(token).then((result) => {
        if (result.success) {
          setIsAdmin(true);
        } else {
          clearAdminToken();
          setIsAdmin(false);
        }
      });
    }
  }, []);

  const handleLogoutAdmin = () => {
    clearAdminToken();
    setIsAdmin(false);
  };

  const handleLoginSuccess = () => {
    setIsAdmin(true);
  };

  // Sync route on popstate / history changes
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
      window.scrollTo(0, 0);
    };

    const handleCustomSearch = () => {
      setIsSearchOpen(true);
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('open-search-modal', handleCustomSearch);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('open-search-modal', handleCustomSearch);
    };
  }, []);

  // Fetch all CMS pages for navigation and parent page assignment
  useEffect(() => {
    fetch('/api/pages')
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setAllPages(Array.isArray(data) ? data : []))
      .catch((e) => console.warn('Could not fetch pages list:', e));
  }, [currentPath]);

  // Scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Route Dispatcher
  const renderCurrentRoute = () => {
    const cleanPath = currentPath.replace(/^\/+|\/+$/g, '');

    // 1. Home
    if (!cleanPath || cleanPath === 'index') {
      return <HomePage isAdmin={isAdmin} allPages={allPages} />;
    }

    // 2. Maps Atlas
    if (cleanPath === 'maps') {
      return <MapsAtlasPage mapSlug="echocondria" />;
    }
    if (cleanPath.startsWith('maps/')) {
      const mapSlug = cleanPath.replace(/^maps\//, '');
      return <MapsAtlasPage mapSlug={mapSlug} />;
    }

    // 3. Known Database Collections
    if (KNOWN_COLLECTIONS.has(cleanPath)) {
      return <CollectionViewPage slug={cleanPath} />;
    }

    // 4. Aliases
    if (cleanPath === 'monsters') {
      return <CollectionViewPage slug="enemies" />;
    }

    // 5. Dynamic CMS Page View
    return <CmsPageView slug={cleanPath} isAdmin={isAdmin} allPages={allPages} />;
  };

  return (
    <div className="d-flex flex-column min-vh-100 site-shell">
      {/* Site Navbar */}
      <Navbar
        isAdmin={isAdmin}
        onLogoutAdmin={handleLogoutAdmin}
        onOpenSearch={() => setIsSearchOpen(true)}
      />

      {/* Main Routed Page Content */}
      <main className="flex-grow-1">{renderCurrentRoute()}</main>

      {/* Footer with subtle Lock Icon */}
      <Footer
        isAdmin={isAdmin}
        onOpenAdminLogin={() => setIsAdminLoginOpen(true)}
        onLogoutAdmin={handleLogoutAdmin}
      />

      {/* Search Modal (Ctrl+K) */}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

      {/* Admin Login Modal */}
      <AdminLoginModal
        isOpen={isAdminLoginOpen}
        onClose={() => setIsAdminLoginOpen(false)}
        onSuccess={handleLoginSuccess}
      />

      {/* Cookie / Theme Consent Banner */}
      <CookieConsent />

      {/* Scroll to top button */}
      {showScrollTop && (
        <button
          type="button"
          className="btn btn-dark rounded-circle shadow position-fixed bottom-0 end-0 m-4 p-3 z-3"
          onClick={scrollToTop}
          title="Scroll to top"
          aria-label="Scroll to top"
        >
          <ArrowUp size={18} />
        </button>
      )}
    </div>
  );
};
